import React from "react";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import { styled } from "@mui/material/styles";

const kindToStyleMap = {
  primary: { variant: "contained", tone: "primary" },
  secondary: { variant: "outlined", tone: "neutral" },
  danger: { variant: "outlined", tone: "danger" },
  ghost: { variant: "text", tone: "neutral" },
};

const toneToColorMap = {
  primary: "primary",
  secondary: "secondary",
  success: "success",
  danger: "error",
  warning: "warning",
  info: "info",
  neutral: "inherit",
};

const resolveKindFromProps = (kind, variant, tone) => {
  if (kind && kindToStyleMap[kind]) {
    return kind;
  }

  if (tone === "danger") {
    return "danger";
  }

  if (variant === "text") {
    return "ghost";
  }

  if (variant === "outlined") {
    return "secondary";
  }

  return "primary";
};

const StyledButton = styled(Button)({
  borderRadius: "var(--radius-md, 8px)",
  fontWeight: "var(--font-weight-semibold, 600)",
  textTransform: "none",
  minHeight: 36,
  padding: "8px 16px",
  fontSize: 13,
  lineHeight: 1.2,
  "&.MuiButton-contained": {
    boxShadow: "none",
    "&:hover": {
      boxShadow: "none",
    },
  },
  "&&.app-button--selected": {
    backgroundColor: "var(--color-action-primary-soft, rgba(35, 159, 198, 0.12))",
    color: "var(--color-action-primary, #239fc6)",
    borderColor: "transparent",
  },
  "&&.app-button--selected:hover": {
    backgroundColor: "var(--color-row-hover-bg, rgba(35, 159, 198, 0.18))",
    borderColor: "transparent",
  },
  "&&.app-button--selected.MuiButton-contained": {
    backgroundColor: "var(--color-action-primary, #239fc6)",
    color: "var(--color-text-on-primary, #ffffff)",
  },
  "&&.app-button--selected.MuiButton-contained:hover": {
    backgroundColor: "var(--color-action-primary-hover, #296f85)",
  },
});

const AppButton = React.forwardRef(function AppButton(
  {
    kind,
    tone = "primary",
    variant = "contained",
    selected = false,
    loading = false,
    loadingLabel,
    disabled = false,
    startIcon,
    endIcon,
    children,
    className,
    ...props
  },
  ref
) {
  const resolvedKind = resolveKindFromProps(kind, variant, tone);
  const defaults = kindToStyleMap[resolvedKind] || kindToStyleMap.primary;

  const resolvedVariant = defaults.variant;
  const resolvedTone = defaults.tone;
  const color = toneToColorMap[resolvedTone] || resolvedTone;
  const resolvedStartIcon = loading ? <CircularProgress size={16} color="inherit" /> : startIcon;
  const resolvedEndIcon = loading ? undefined : endIcon;

  const resolvedClassName = `${className ? `${className} ` : ""}app-button app-button--${resolvedKind} ${
    selected ? "app-button--selected" : ""
  }`;

  return (
    <StyledButton
      ref={ref}
      className={resolvedClassName}
      color={color}
      variant={resolvedVariant}
      disabled={disabled || loading}
      startIcon={resolvedStartIcon}
      endIcon={resolvedEndIcon}
      {...props}
    >
      {loading ? loadingLabel || children : children}
    </StyledButton>
  );
});

AppButton.muiName = Button.muiName;

export default AppButton;
